import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useParams } from '@tanstack/react-router';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Breadcrumbs, PageHeader, Table } from '~/components/data';
import { EntityAuditPanel } from '~/components/domain';
import { ProjectRefChip } from '~/components/domain/project/ProjectRefChip';
import { useToast } from '~/components/feedback';
import { Button, Checkbox, Dialog, Input, Select } from '~/components/primitives';
import { DocStatusBadge, type DocStatusValue, docStatusLabel } from '~/components/status';
import { CancelPreviewViewer } from '~/components/stock';
import { ApiRequestError } from '~/lib/api/client';
import { renderApiError } from '~/lib/api/error-renderer';
import {
  cancelPreviewStockEntry,
  cancelStockEntry,
  fetchStockCancelApprovers,
  fetchStockEntry,
  submitPreviewStockEntry,
  submitStockEntry,
} from '~/lib/api/stock-entries';
import { validateAuditHighlight } from '~/lib/audit/highlight-search';
import { formatDateTime, formatDecimal, formatMoney, formatQty, formatUom } from '~/lib/format';
import { STOCK_ENTRY_TYPE_LABELS, labelOf } from '~/lib/i18n/labels';
import { getRoleLabel } from '~/lib/rbac';
import { canDo, useUserScope } from '~/lib/rbac/use-user-scope';
import { useAuthStore } from '~/store/auth';
import shared from './_auth._shared.module.css';
import styles from './_auth.stock-entries.$id.module.css';

export const Route = createFileRoute('/_auth/stock-entries/$id/')({
  // F105 (С-I): `?highlight=<audit_event_id>` — запись журнала, которую открыть в панели истории.
  validateSearch: validateAuditHighlight,
  staticData: { minMainWidth: 960 },
  component: StockEntryDetail,
});

function StockEntryDetail(): JSX.Element {
  const { id } = useParams({ from: '/_auth/stock-entries/$id/' });
  const { highlight: auditHighlight } = Route.useSearch();
  const qc = useQueryClient();
  const toast = useToast();
  const me = useAuthStore((s) => s.user);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelApprover, setCancelApprover] = useState('');
  const [overrideClosed, setOverrideClosed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [periodLockError, setPeriodLockError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['stock-entries', id],
    queryFn: () => fetchStockEntry(id),
  });

  const submitPreview = useQuery({
    queryKey: ['stock-entries', id, 'submit-preview'],
    queryFn: () => submitPreviewStockEntry(id),
    enabled: submitOpen,
  });

  const cancelPreview = useQuery({
    queryKey: ['stock-entries', id, 'cancel-preview'],
    queryFn: () => cancelPreviewStockEntry(id),
    enabled: cancelOpen,
  });

  // B59 / WHS-B14 — кандидаты по строке матрицы `stock_entry_cancel` (раньше — админский
  // `/admin/users` с фильтром «контролёр или админ», пустой под кладовщиком).
  const approversQuery = useQuery({
    queryKey: ['stock-entries', 'cancel-approvers', 'stock_entry', id],
    queryFn: () => fetchStockCancelApprovers({ type: 'stock_entry', id }),
    enabled: cancelOpen,
  });
  /** WHS-B14 (UX, круг 1) — кто согласовал, в тосте успеха. */
  const approverChosen = (approversQuery.data?.items ?? []).find((u) => u.id === cancelApprover);
  const approverName = approverChosen ? (approverChosen.full_name ?? approverChosen.email) : null;

  function handleMutationError(err: unknown, title: string): void {
    if (err instanceof ApiRequestError && err.code === 'PERIOD_LOCKED') {
      // Period-lock inline banner — structural code reused, без freeform текста.
      setPeriodLockError('period-locked');
    } else {
      const rendered = renderApiError(err);
      toast.push({ variant: 'error', title, description: rendered.body });
    }
  }

  const submitMut = useMutation({
    mutationFn: () => submitStockEntry(id),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.route_se_detail_toast_submitted() });
      void qc.invalidateQueries({ queryKey: ['stock-entries', id] });
      setSubmitOpen(false);
    },
    onError: (err) => handleMutationError(err, m.route_se_detail_toast_submit_failed()),
  });

  const cancelMut = useMutation({
    mutationFn: () => cancelStockEntry(id, cancelReason.trim(), { approver_id: cancelApprover }),
    onSuccess: () => {
      toast.push({
        variant: 'success',
        title: m.route_se_detail_toast_cancelled(),
        ...(approverName
          ? { description: m.stock_cancel_toast_approved_by({ name: approverName }) }
          : {}),
      });
      void qc.invalidateQueries({ queryKey: ['stock-entries', id] });
      setCancelOpen(false);
      setCancelReason('');
      setCancelApprover('');
      setOverrideClosed(false);
      setAcknowledged(false);
    },
    onError: (err) => handleMutationError(err, m.route_se_detail_toast_cancel_failed()),
  });

  const isDraft = data?.status === 'draft';
  const isSubmitted = data?.status === 'submitted';
  const scope = useUserScope();
  const canEdit = canDo(scope, 'stock_entry', 'update');
  const subPreview = submitPreview.data;
  const subBlocking =
    !!subPreview &&
    ((subPreview.negative_stock_violations ?? []).some((v) => !v.allow_negative) ||
      (subPreview.warnings ?? []).some((w) => w.blocking));

  const cancelBlocking =
    !!cancelPreview.data &&
    (cancelPreview.data.warnings ?? []).some((w) => w.blocking && !overrideClosed);
  const hasCriticalCancel =
    !!cancelPreview.data &&
    (cancelPreview.data.warnings ?? []).some((w) => w.severity === 'danger');
  const reasonValid = cancelReason.trim().length >= 10;

  const isAdmin = me?.role === 'admin';
  const eligibleApprovers = approversQuery.data?.items ?? [];

  return (
    <div className={shared.page}>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.nav_stock_entries(), to: '/stock-entries' },
              { label: data ? (data.voucher_no ?? data.id.slice(0, 8)) : '…' },
            ]}
          />
        }
        // FE-71: prefer voucher_no over UUID prefix для human-readable identity.
        title={
          data
            ? m.route_se_detail_title_with_id({ id: data.voucher_no ?? data.id.slice(0, 8) })
            : m.route_se_detail_title_fallback()
        }
        description={
          data ? (
            <>
              {/* FE-291 Pass 62: localize entry_type + status вместо raw enums. */}
              {m.route_se_detail_description({
                type: labelOf(
                  STOCK_ENTRY_TYPE_LABELS,
                  data.entry_type as keyof typeof STOCK_ENTRY_TYPE_LABELS,
                ),
                status: docStatusLabel(data.status as DocStatusValue),
              })}
              {(data as { project_id?: string | null }).project_id ? (
                <>
                  {' '}
                  <ProjectRefChip
                    projectId={(data as { project_id?: string | null }).project_id ?? null}
                  />
                </>
              ) : null}
            </>
          ) : undefined
        }
        actions={
          data && (
            <div className={shared.actions}>
              {isDraft && canEdit && (
                <Link to="/stock-entries/$id/edit" params={{ id }}>
                  <Button variant="secondary">{m.common_button_edit()}</Button>
                </Link>
              )}
              {/*
                Действие — только там, где применимо (правило WHS-F34): у проведённого
                документа неактивная «Провести» читалась как «не проведён».
              */}
              {isDraft && (
                <Button
                  variant="primary"
                  leadingIcon={<CheckCircle2 size={14} />}
                  onClick={() => setSubmitOpen(true)}
                >
                  {m.route_se_detail_btn_submit()}
                </Button>
              )}
              {isSubmitted && (
                <Button
                  variant="destructive"
                  leadingIcon={<XCircle size={14} />}
                  onClick={() => setCancelOpen(true)}
                >
                  {m.route_se_detail_btn_cancel()}
                </Button>
              )}
            </div>
          )
        }
      />

      {periodLockError && (
        <div className={styles.lockBanner} role="alert">
          <AlertTriangle size={16} aria-hidden="true" />
          <div>
            <strong>{m.route_se_detail_period_locked_title()}</strong> {periodLockError}
          </div>
          <Link to="/period-close" className={shared.link}>
            {m.route_se_detail_period_locked_link()}
          </Link>
        </div>
      )}

      {/*
        WHS-B3: документ проведён с расходом по нулевой оценке. Себестоимость таких
        строк не определена — это говорится словами, а не показывается нулём.
      */}
      {data?.valued_at_zero && (
        <div className={styles.zeroValuationBanner} role="status">
          <AlertTriangle size={16} aria-hidden="true" />
          <div>
            <strong>{m.se_valued_at_zero_title()}</strong> {m.se_valued_at_zero_body()}
          </div>
        </div>
      )}

      {/* FE-73: meta block parity with SR detail. */}
      {data && (
        <div className={shared.statGrid4}>
          <div>
            <div className={shared.statLabel}>{m.ui_tip()}</div>
            <div>
              {labelOf(
                STOCK_ENTRY_TYPE_LABELS,
                data.entry_type as keyof typeof STOCK_ENTRY_TYPE_LABELS,
              )}
            </div>
          </div>
          <div>
            <div className={shared.statLabel}>{m.ui_status()}</div>
            <div>
              <DocStatusBadge status={data.status as DocStatusValue} size="sm" />
            </div>
          </div>
          <div>
            <div className={shared.statLabel}>{m.ui_data_provodki()}</div>
            <div className={shared.mono}>
              {data.posting_at ? formatDateTime(data.posting_at) : '—'}
            </div>
          </div>
          <div>
            <div className={shared.statLabel}>{m.ui_strok()}</div>
            <div className={shared.mono}>{data.lines?.length ?? 0}</div>
          </div>
        </div>
      )}

      {data && (
        <Table
          ariaLabel={m.route_se_detail_lines_aria()}
          data={data.lines ?? []}
          rowKey={(_, i) => String(i)}
          columns={[
            {
              // FE-292 Pass 93: use denorm name from BE if available.
              id: 'item',
              header: m.route_se_detail_col_item(),
              accessor: (l) => l.item_name ?? l.item_sku ?? l.item_id.slice(0, 8),
            },
            {
              id: 'src',
              header: m.route_se_detail_col_source(),
              accessor: (l) => l.source_warehouse_name ?? l.source_warehouse_id?.slice(0, 8) ?? '—',
            },
            {
              id: 'tgt',
              header: m.route_se_detail_col_target(),
              // FE-72: in-place repack marker (↻) when source = target warehouse.
              accessor: (l) => {
                const tgt = l.target_warehouse_name ?? l.target_warehouse_id?.slice(0, 8) ?? '—';
                const isRepack =
                  data?.entry_type === 'repack' &&
                  l.source_warehouse_id &&
                  l.source_warehouse_id === l.target_warehouse_id;
                return isRepack ? <span title="In-place repack">↻ {tgt}</span> : tgt;
              },
            },
            {
              id: 'qty',
              header: m.route_se_detail_col_qty(),
              // FE-293 Pass 62: formatDecimal обрезает trailing zeros (10 digits → 3-4).
              accessor: (l) => formatDecimal(l.qty),
              mono: true,
              align: 'right' as const,
            },
            // Единица — словом («шт»), не кодом («pcs»).
            { id: 'uom', header: m.route_se_detail_col_uom(), accessor: (l) => formatUom(l.uom) },
            {
              id: 'batch',
              header: m.route_se_detail_col_batch(),
              accessor: (l) => l.batch_no ?? '—',
              mono: true,
            },
            // WHS-B3: колонка оценки — только у документа с нулевой оценкой.
            ...(data.valued_at_zero
              ? [
                  {
                    id: 'zero',
                    header: m.se_valued_at_zero_col(),
                    accessor: (l: (typeof data.lines)[number]) =>
                      l.valued_at_zero ? (
                        <span className={styles.zeroValuationMark}>
                          {m.se_valued_at_zero_chip()}
                        </span>
                      ) : (
                        '—'
                      ),
                  },
                ]
              : []),
          ]}
        />
      )}

      <EntityAuditPanel entity_type="stock_entry" entity_id={id} highlight={auditHighlight} />

      <Dialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        title={m.route_se_detail_submit_title()}
        size="lg"
        footer={
          <div className={styles.dialogFooter}>
            <Button variant="ghost" onClick={() => setSubmitOpen(false)}>
              {m.route_se_detail_submit_back()}
            </Button>
            <Button
              variant="primary"
              disabled={submitMut.isPending || subBlocking}
              loading={submitMut.isPending}
              onClick={() => submitMut.mutate()}
            >
              {subBlocking
                ? m.route_se_detail_submit_blocked()
                : m.route_se_detail_submit_confirm()}
            </Button>
          </div>
        }
      >
        <p className={shared.subtle}>{m.route_se_detail_submit_hint()}</p>
        <Table
          ariaLabel={m.route_se_detail_submit_preview_aria()}
          loading={submitPreview.isLoading}
          // F1: ручка отдаёт `expected_sle`, а не `rows` — прежняя схема не
          // совпадала с ней ни одним полем, и таблица никогда не заполнялась.
          data={subPreview?.expected_sle ?? []}
          rowKey={(_, i) => String(i)}
          emptyTitle={m.route_se_detail_preview_empty()}
          columns={[
            {
              id: 'item',
              header: m.route_se_detail_col_item(),
              accessor: (r) => r.item_id.slice(0, 8),
              mono: true,
            },
            {
              id: 'wh',
              header: m.route_se_detail_col_wh(),
              accessor: (r) => r.warehouse_id.slice(0, 8),
              mono: true,
            },
            {
              id: 'batch',
              header: m.route_se_detail_col_batch(),
              accessor: (r) => r.batch_id?.slice(0, 8) ?? '—',
              mono: true,
            },
            {
              id: 'qty',
              header: m.route_se_detail_col_qty_delta(),
              accessor: (r) => formatQty(r.qty_change),
              mono: true,
              align: 'right',
            },
            {
              id: 'rate',
              header: m.route_se_detail_col_rate(),
              accessor: (r) => formatMoney(r.valuation_rate),
              mono: true,
              align: 'right',
            },
            {
              id: 'val',
              header: m.route_se_detail_col_value_delta(),
              accessor: (r) => formatMoney(r.balance_value_after),
              mono: true,
              align: 'right',
            },
          ]}
        />
        {(subPreview?.negative_stock_violations ?? []).length > 0 && (
          <div role="alert" className={styles.violationAlert}>
            <strong>{m.route_se_detail_violations_heading()}</strong>
            <ul className={styles.violationList}>
              {(subPreview?.negative_stock_violations ?? []).map((v) => (
                <li key={`${v.item_id}:${v.warehouse_id}`}>
                  {m.route_se_detail_violation_row({
                    item: v.item_label ?? v.item_id.slice(0, 8),
                    warehouse: v.warehouse_label ?? v.warehouse_id.slice(0, 8),
                    current: String(v.current_qty),
                    required: String(v.required_qty),
                  })}
                  {!v.allow_negative && m.route_se_detail_violation_block_suffix()}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Dialog>

      <Dialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={m.route_se_detail_cancel_title()}
        size="lg"
        footer={
          <div className={styles.dialogFooter}>
            <Button variant="ghost" onClick={() => setCancelOpen(false)}>
              {m.route_se_detail_cancel_back()}
            </Button>
            <Button
              variant="destructive"
              disabled={
                !reasonValid ||
                !cancelApprover ||
                cancelMut.isPending ||
                (cancelBlocking && !isAdmin) ||
                (hasCriticalCancel && !acknowledged)
              }
              loading={cancelMut.isPending}
              onClick={() => cancelMut.mutate()}
            >
              {m.route_se_detail_cancel_confirm()}
            </Button>
          </div>
        }
      >
        <CancelPreviewViewer
          data={cancelPreview.data}
          loading={cancelPreview.isLoading}
          acknowledged={acknowledged}
          onAcknowledgeChange={setAcknowledged}
        />
        <div className={styles.cancelGrid}>
          {/*
           * WHS-B14, круг 1 гейта (UX): у выбора есть исход «выбрать некого» и «список не
           * загрузился» — экран называет причину и путь, кнопка не остаётся серой молча.
           */}
          <Select
            label={m.route_se_detail_approver_label()}
            value={cancelApprover}
            onChange={(e) => setCancelApprover(e.target.value)}
            placeholder={m.route_se_detail_approver_choose()}
            disabled={approversQuery.isLoading}
            options={eligibleApprovers.map((u) => ({
              value: u.id,
              label: m.route_se_detail_approver_role_user({
                name: u.full_name ?? u.email,
                role: getRoleLabel(u.role),
              }),
            }))}
            hint={m.route_se_detail_approver_hint()}
            {...(approversQuery.isError
              ? { error: m.stock_cancel_approvers_load_failed() }
              : approversQuery.isSuccess && eligibleApprovers.length === 0
                ? { error: m.stock_cancel_no_approvers() }
                : {})}
          />
          {approversQuery.isError && (
            <Button variant="ghost" size="sm" onClick={() => void approversQuery.refetch()}>
              {m.common_button_retry()}
            </Button>
          )}
          <Input
            label={m.route_se_detail_reason_label()}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder={m.route_se_detail_reason_placeholder()}
            error={
              cancelReason.length > 0 && !reasonValid
                ? m.route_se_detail_reason_err_min()
                : undefined
            }
          />
          {isAdmin && (
            <Checkbox
              label={m.route_se_detail_override_label()}
              checked={overrideClosed}
              onChange={(e) => setOverrideClosed(e.target.checked)}
            />
          )}
        </div>
      </Dialog>
    </div>
  );
}
